import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Box,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import StarIcon from "@mui/icons-material/Star";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { makeStyles } from "tss-react/mui";
import { useMealPlanGenerator } from "../../hooks/use-meal-plan-generator";
import { useMealPlanner } from "../../context-providers/meal-planner-provider";
import { useHome } from "../../context-providers/home-provider";
import { useRecipeManager } from "../../context-providers/recipe-manager-provider";
import { useSnackbar } from "notistack";
import { Meal, Recipe } from "../../types";
import { useDragReorder } from "../../hooks/use-drag-reorder";
import { useAutoScroll } from "../../hooks/useAutoScroll";
import FavouritesPickerDialog from "../dialog/favourites-picker-dialog";

const useStyles = makeStyles()((theme) => ({
  stepContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    minHeight: "300px",
  },
  datePickerContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  previewContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    maxHeight: "600px",
    overflowY: "auto",
  },
  mealCard: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0.5),
    position: "relative",
    border: `2px dashed transparent`,
    transition: "border-color 0.2s, background-color 0.2s",
  },
  dropIndicator: {
    borderColor: theme.palette.primary.main,
    borderWidth: "2px",
    borderStyle: "dashed",
    backgroundColor: theme.palette.action.hover,
  },
  dragHandle: {
    cursor: "grab",
    color: theme.palette.text.secondary,
    "&:active": {
      cursor: "grabbing",
    },
  },
  mealCardContent: {
    flex: 1,
    padding: theme.spacing(0.5),
  },
  mealDate: {
    paddingInline: theme.spacing(0.5),
    whiteSpace: "wrap",
    color: theme.palette.text.secondary,
  },
  rerollButton: {
    marginLeft: theme.spacing(1),
  },
  labelsContainer: {
    display: "flex",
    gap: theme.spacing(0.5),
    flexWrap: "wrap",
    marginTop: theme.spacing(0.5),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
  },
  dragGhost: {
    position: "fixed",
    pointerEvents: "none",
    zIndex: 9999,
    opacity: 0.8,
    transform: "rotate(-2deg) scale(0.95)",
    boxShadow: theme.shadows[8],
  },
}));

interface AutoPlanDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function AutoPlanDialog({ open, onClose }: AutoPlanDialogProps) {
  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { addUserMeal, addHomeMeal } = useMealPlanner();
  const { home } = useHome();
  const { userRecipes, homeRecipes } = useRecipeManager();
  const {
    generatedPlan,
    error,
    isGenerating,
    generate,
    clear,
    rerollRecipe,
    replaceMealWithRecipe,
    reorderPlan,
  } = useMealPlanGenerator();

  // Combine user and home recipes for favourites picker
  const allRecipes = [...(userRecipes || []), ...(homeRecipes || [])];

  const [step, setStep] = useState<"select-dates" | "preview">("select-dates");
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs());
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().add(6, "day"));
  const [isAdding, setIsAdding] = useState(false);
  const [favouritesDialogOpen, setFavouritesDialogOpen] = useState(false);
  const [selectedMealIndex, setSelectedMealIndex] = useState<number | null>(null);

  // Drag and drop functionality
  const {
    draggedIndex,
    dragOverIndex,
    ghostPosition,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  } = useDragReorder(reorderPlan);

  const previewContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll hook
  const { updateCursorPosition, startAutoScroll, stopAutoScroll } = useAutoScroll({
    scrollContainerRef: previewContainerRef,
    axis: 'vertical',
    zoneSize: 80,
    maxSpeed: 10,
    enabled: draggedIndex !== null,
  });

  // Wrapped drag handlers for desktop with auto-scroll
  const handleDragStartWithScroll = (index: number) => (e: React.DragEvent) => {
    handleDragStart(index)(e);
    startAutoScroll();
  };

  const handleDragOverWithScroll = (index: number) => (e: React.DragEvent) => {
    handleDragOver(index)(e);
    updateCursorPosition(e.clientX, e.clientY);
  };

  const handleDragEndWithScroll = () => {
    handleDragEnd();
    stopAutoScroll();
  };

  const handleDropWithScroll = (index: number) => (e: React.DragEvent) => {
    handleDrop(index)(e);
    stopAutoScroll();
  };

  // Add touch event listeners with { passive: false } to allow preventDefault
  useEffect(() => {
    const container = previewContainerRef.current;
    if (!container) return;

    const onTouchMove = (e: TouchEvent) => {
      if (draggedIndex !== null) {
        e.preventDefault();
        handleTouchMove(e as any);

        // Update cursor position for auto-scroll
        const touch = e.touches[0];
        updateCursorPosition(touch.clientX, touch.clientY);
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (draggedIndex !== null) {
        handleTouchEnd(e as any);
        stopAutoScroll();
      }
    };

    // Only add listeners when dragging
    if (draggedIndex !== null) {
      container.addEventListener("touchmove", onTouchMove, { passive: false });
      container.addEventListener("touchend", onTouchEnd, { passive: false });
      startAutoScroll();

      return () => {
        container.removeEventListener("touchmove", onTouchMove);
        container.removeEventListener("touchend", onTouchEnd);
        stopAutoScroll();
      };
    }
  }, [draggedIndex, handleTouchMove, handleTouchEnd, updateCursorPosition, startAutoScroll, stopAutoScroll]);

  const isHome = home && !!home.id;

  const handleClose = () => {
    setStep("select-dates");
    setStartDate(dayjs());
    setEndDate(dayjs().add(6, "day"));
    clear();
    onClose();
  };

  const handleGenerate = () => {
    if (!startDate || !endDate) {
      enqueueSnackbar("Please select both start and end dates", {
        variant: "warning",
      });
      return;
    }

    const daysDiff = endDate.diff(startDate, "day") + 1;

    if (daysDiff <= 0) {
      enqueueSnackbar("End date must be after start date", {
        variant: "warning",
      });
      return;
    }

    if (daysDiff > 10) {
      enqueueSnackbar("Date range cannot exceed 10 days", {
        variant: "warning",
      });
      return;
    }

    generate(startDate.toDate(), endDate.toDate());
    setStep("preview");
  };

  const handleRegenerate = () => {
    if (startDate && endDate) {
      generate(startDate.toDate(), endDate.toDate());
    }
  };

  const handleConfirm = async () => {
    if (!generatedPlan) return;

    setIsAdding(true);

    try {
      const addMealFunction = isHome ? addHomeMeal : addUserMeal;

      for (const plannedMeal of generatedPlan) {
        const meal: Meal = {
          ...plannedMeal.recipe,
          date: plannedMeal.date,
          originalServes: plannedMeal.recipe.serves,
        };

        await addMealFunction(meal);
      }

      enqueueSnackbar(`Added ${generatedPlan.length} meals to your plan!`, {
        variant: "success",
      });

      handleClose();
    } catch (err) {
      console.error("Error adding meals:", err);
      enqueueSnackbar("Failed to add meals to calendar", { variant: "error" });
    } finally {
      setIsAdding(false);
    }
  };

  const handleBack = () => {
    setStep("select-dates");
    clear();
  };

  const handleFavouriteSelected = (recipe: Recipe) => {
    // If we already have a selected meal index, replace it immediately
    if (selectedMealIndex !== null && generatedPlan) {
      replaceMealWithRecipe(selectedMealIndex, recipe);
      setSelectedMealIndex(null);
    }
  };

  const canGenerate =
    startDate && endDate && endDate.diff(startDate, "day") >= 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {step === "select-dates" ? "Auto-Plan Meals" : "Review Meal Plan"}
      </DialogTitle>

      <DialogContent>
        {step === "select-dates" && (
          <div className={classes.stepContainer}>
            <Typography variant="body2" color="textSecondary">
              Select a date range (up to 10 days) and we'll generate a varied
              meal plan for you.
            </Typography>

            <div className={classes.datePickerContainer}>
              <DatePicker
                label="Start Date"
                format="DD MMM YYYY"
                value={startDate}
                onChange={(date) => setStartDate(date)}
                disablePast
              />

              <DatePicker
                label="End Date"
                format="DD MMM YYYY"
                value={endDate}
                onChange={(date) => setEndDate(date)}
                disablePast
                minDate={startDate || undefined}
              />

              {startDate && endDate && (
                <Typography variant="body2" color="textSecondary">
                  {endDate.diff(startDate, "day") + 1} meals will be generated
                </Typography>
              )}
            </div>
          </div>
        )}

        {step === "preview" && (
          <div className={classes.stepContainer}>
            {isGenerating && (
              <div className={classes.loadingContainer}>
                <CircularProgress />
              </div>
            )}

            {error && (
              <Alert severity="error" onClose={clear}>
                {error}
              </Alert>
            )}

            {generatedPlan && generatedPlan.length > 0 && (
              <div
                ref={previewContainerRef}
                className={classes.previewContainer}
              >
                {generatedPlan.map((plannedMeal, index) => (
                  <Card
                    key={index}
                    data-drag-index={index}
                    className={`${classes.mealCard} ${
                      dragOverIndex === index && draggedIndex !== index
                        ? classes.dropIndicator
                        : ""
                    }`}
                    elevation={1}
                    onDragOver={handleDragOverWithScroll(index)}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDropWithScroll(index)}
                    style={{
                      opacity: draggedIndex === index ? 0.5 : 1,
                    }}
                  >
                    <div
                      draggable
                      onDragStart={handleDragStartWithScroll(index)}
                      onDragEnd={handleDragEndWithScroll}
                      onTouchStart={handleTouchStart(index)}
                      style={{ display: "flex" }}
                    >
                      <DragIndicatorIcon className={classes.dragHandle} />
                    </div>

                    <CardContent className={classes.mealCardContent}>
                      <Typography variant="body1" fontWeight="medium">
                        {plannedMeal.recipe.name}
                      </Typography>

                      {plannedMeal.recipe.labels &&
                        plannedMeal.recipe.labels.length > 0 && (
                          <div className={classes.labelsContainer}>
                            {plannedMeal.recipe.labels
                              .slice(0, 2)
                              .map((label, i) => (
                                <Chip key={i} label={label} size="small" />
                              ))}
                            {plannedMeal.recipe.labels.length > 2 && (
                              <Chip
                                label={`+${
                                  plannedMeal.recipe.labels.length - 2
                                }`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </div>
                        )}
                    </CardContent>

                    <div className={classes.mealDate}>
                      <Typography variant="body2">
                        {dayjs(plannedMeal.date).format("ddd")}
                      </Typography>
                      <Typography variant="body2">
                        {dayjs(plannedMeal.date).format("MMM D")}
                      </Typography>
                    </div>

                    <IconButton
                      className={classes.rerollButton}
                      onClick={() => rerollRecipe(index)}
                      size="small"
                      title="Reroll this recipe"
                      disabled={isAdding}
                    >
                      <RefreshIcon />
                    </IconButton>

                    <IconButton
                      onClick={() => {
                        setSelectedMealIndex(index);
                        setFavouritesDialogOpen(true);
                      }}
                      size="small"
                      title="Pick from favourites"
                      disabled={isAdding}
                    >
                      <StarIcon fontSize="small" />
                    </IconButton>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Ghost element for mobile drag */}
        {ghostPosition && draggedIndex !== null && generatedPlan && (
          <Card
            className={classes.dragGhost}
            elevation={8}
            style={{
              left: ghostPosition.x - 150,
              top: ghostPosition.y - 30,
              width: "300px",
            }}
          >
            <div
              style={{ display: "flex", alignItems: "center", padding: "8px" }}
            >
              <DragIndicatorIcon className={classes.dragHandle} />
              <Typography
                variant="body2"
                style={{
                  minWidth: "80px",
                  textAlign: "right",
                  paddingRight: "16px",
                }}
              >
                {dayjs(generatedPlan[draggedIndex].date).format("ddd, MMM D")}
              </Typography>
              <CardContent style={{ flex: 1, padding: "8px 16px" }}>
                <Typography variant="subtitle2" fontWeight="medium">
                  {generatedPlan[draggedIndex].recipe.name}
                </Typography>
              </CardContent>
            </div>
          </Card>
        )}
      </DialogContent>

      <DialogActions>
        {step === "select-dates" && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              onClick={handleGenerate}
              variant="contained"
              color="primary"
              disabled={!canGenerate}
            >
              Generate Plan
            </Button>
          </>
        )}

        {step === "preview" && (
          <>
            <Button onClick={handleBack} disabled={isAdding}>
              Back
            </Button>
            <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
              <Button
                onClick={handleRegenerate}
                disabled={isAdding || isGenerating}
              >
                Regenerate
              </Button>
              <Button
                onClick={handleConfirm}
                variant="contained"
                color="primary"
                disabled={!generatedPlan || isAdding || isGenerating}
              >
                {isAdding ? <CircularProgress size={24} /> : "Add to Calendar"}
              </Button>
            </Box>
          </>
        )}
      </DialogActions>

      {/* Favourites Picker Dialog */}
      <FavouritesPickerDialog
        open={favouritesDialogOpen}
        handleClose={() => {
          setFavouritesDialogOpen(false);
          setSelectedMealIndex(null);
        }}
        onSelectRecipe={handleFavouriteSelected}
        recipes={allRecipes}
        excludeRecipeIds={generatedPlan?.map((m) => m.recipe.recipeId) || []}
      />
    </Dialog>
  );
}
